import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Modal,
  message,
  Badge,
  Avatar,
  Dropdown,
  Menu,
  Select
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilterOutlined,
  ExportOutlined,
  ReloadOutlined,
  ToolOutlined,
  SettingOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const EquipmentList = () => {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [blocks, setBlocks] = useState([]);

  const categories = [
    { value: 'all', label: 'All Categories', color: 'default' },
    { value: 'excavator', label: 'Excavators', color: 'blue' },
    { value: 'adt', label: 'ADTs', color: 'green' },
    { value: 'dozer', label: 'Dozers', color: 'orange' },
    { value: 'grader', label: 'Graders', color: 'purple' },
    { value: 'watercart', label: 'Watercarts', color: 'cyan' },
    { value: 'fel', label: 'FELs', color: 'red' },
    { value: 'crusher', label: 'Crushers', color: 'geekblue' },
    { value: 'screener', label: 'Screeners', color: 'magenta' }
  ];

  const statuses = [
    { value: 'all', label: 'All Status', color: 'default' },
    { value: 'available', label: 'Available', color: 'green' },
    { value: 'in_use', label: 'In Use', color: 'blue' },
    { value: 'maintenance', label: 'Maintenance', color: 'orange' },
    { value: 'out_of_service', label: 'Out of Service', color: 'red' }
  ];

  useEffect(() => {
    fetchEquipment();
    fetchBlocks();
  }, []);

  useEffect(() => {
    filterEquipment();
  }, [equipment, searchText, selectedCategory, selectedStatus, selectedBlock]);

  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const response = await api.get('/equipment');
      setEquipment(response.data);
    } catch (error) {
      message.error('Failed to fetch equipment');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocks = async () => {
    try {
      const response = await api.get('/blocks');
      setBlocks(response.data);
    } catch (error) {
      console.error('Failed to fetch blocks:', error);
    }
  };

  const filterEquipment = () => {
    let filtered = [...equipment];

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(item =>
        item.equipment_code?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.model?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.type_name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }

    // Filter by block
    if (selectedBlock !== 'all') {
      filtered = filtered.filter(item => item.current_block_id === parseInt(selectedBlock));
    }

    setFilteredEquipment(filtered);
  };

  const getStatusTag = (status) => {
    const config = {
      available: { color: 'success', icon: <CheckCircleOutlined />, text: 'Available' },
      in_use: { color: 'processing', icon: <ToolOutlined />, text: 'In Use' },
      maintenance: { color: 'warning', icon: <WarningOutlined />, text: 'Maintenance' },
      out_of_service: { color: 'error', icon: <CloseCircleOutlined />, text: 'Out of Service' }
    };
    
    const cfg = config[status] || config.available;
    return (
      <Tag color={cfg.color} icon={cfg.icon}>
        {cfg.text}
      </Tag>
    );
  };

  const getCategoryTag = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? (
      <Tag color={cat.color}>{cat.label}</Tag>
    ) : (
      <Tag>{category}</Tag>
    );
  };

  const getMaintenanceStatus = (item) => {
    if (!item.next_maintenance_date) return null;
    
    const nextMaintenance = new Date(item.next_maintenance_date);
    const today = new Date();
    const daysUntil = Math.ceil((nextMaintenance - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil <= 0) {
      return { status: 'overdue', text: 'Maintenance Overdue', color: '#ff4d4f' };
    } else if (daysUntil <= 7) {
      return { status: 'due_soon', text: `Due in ${daysUntil} days`, color: '#fa8c16' };
    } else {
      return { status: 'ok', text: `Due in ${daysUntil} days`, color: '#52c41a' };
    }
  };

  const columns = [
    {
      title: 'Equipment',
      dataIndex: 'equipment_code',
      key: 'equipment_code',
      width: 150,
      render: (text, record) => (
        <Space>
          <Avatar
            size="large"
            style={{
              backgroundColor: 
                record.status === 'available' ? '#52c41a' :
                record.status === 'in_use' ? '#1890ff' :
                record.status === 'maintenance' ? '#fa8c16' : '#ff4d4f',
              fontSize: 16,
              fontWeight: 'bold'
            }}
          >
            {record.type_name?.charAt(0) || 'E'}
          </Avatar>
          <Space direction="vertical" size={2}>
            <Text strong style={{ fontSize: 14 }}>{text}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.type_name}
            </Text>
          </Space>
        </Space>
      ),
      sorter: (a, b) => a.equipment_code.localeCompare(b.equipment_code)
    },
    {
      title: 'Model & Size',
      key: 'model_size',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>{record.model || 'N/A'}</Text>
          <Text type="secondary">Size: {record.size || 'N/A'}</Text>
        </Space>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => getCategoryTag(category),
      filters: categories.slice(1).map(c => ({ text: c.label, value: c.value })),
      onFilter: (value, record) => record.category === value
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
      filters: statuses.slice(1).map(s => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Location',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.block_name ? (
            <>
              <Tag color="blue">Assigned to</Tag>
              <Text strong>{record.block_name}</Text>
            </>
          ) : (
            <Tag color="default">Unassigned</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Maintenance',
      key: 'maintenance',
      width: 150,
      render: (_, record) => {
        const maintenance = getMaintenanceStatus(record);
        return maintenance ? (
          <Space direction="vertical" size={2}>
            <Text style={{ color: maintenance.color }}>{maintenance.text}</Text>
            {record.last_maintenance_date && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Last: {new Date(record.last_maintenance_date).toLocaleDateString()}
              </Text>
            )}
          </Space>
        ) : (
          <Text type="secondary">No schedule</Text>
        );
      }
    },
    {
      title: 'Fuel',
      key: 'fuel',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>
            {record.current_fuel_level || 0} / {record.fuel_capacity || 0} L
          </Text>
          <Progress 
            percent={record.fuel_capacity ? 
              Math.round((record.current_fuel_level || 0) / record.fuel_capacity * 100) : 0
            }
            size="small"
            strokeColor={
              (record.current_fuel_level || 0) / (record.fuel_capacity || 1) < 0.2 ? '#ff4d4f' :
              (record.current_fuel_level || 0) / (record.fuel_capacity || 1) < 0.5 ? '#fa8c16' : '#52c41a'
            }
          />
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/production/equipment/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/production/equipment/${record.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Quick Actions">
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'assign',
                    label: 'Assign to Block',
                    onClick: () => handleAssign(record)
                  },
                  {
                    key: 'maintenance',
                    label: 'Schedule Maintenance',
                    onClick: () => handleMaintenance(record)
                  },
                  {
                    key: 'refuel',
                    label: 'Record Refuel',
                    onClick: () => handleRefuel(record)
                  },
                  { type: 'divider' },
                  {
                    key: 'disable',
                    label: 'Disable Equipment',
                    danger: true,
                    onClick: () => handleDisable(record)
                  }
                ]
              }}
              trigger={['click']}
            >
              <Button type="text" icon={<SettingOutlined />} />
            </Dropdown>
          </Tooltip>
        </Space>
      )
    }
  ];

  const handleAssign = async (equipment) => {
    Modal.info({
      title: 'Assign Equipment',
      content: 'Assign equipment functionality coming soon',
    });
  };

  const handleMaintenance = async (equipment) => {
    Modal.info({
      title: 'Schedule Maintenance',
      content: 'Maintenance scheduling functionality coming soon',
    });
  };

  const handleRefuel = async (equipment) => {
    Modal.info({
      title: 'Record Refuel',
      content: 'Refuel recording functionality coming soon',
    });
  };

  const handleDisable = async (equipment) => {
    Modal.confirm({
      title: 'Disable Equipment',
      content: `Are you sure you want to disable ${equipment.equipment_code}?`,
      onOk: async () => {
        try {
          await api.put(`/equipment/${equipment.id}`, { status: 'out_of_service' });
          message.success('Equipment disabled successfully');
          fetchEquipment();
        } catch (error) {
          message.error('Failed to disable equipment');
        }
      }
    });
  };

  const getStatistics = () => {
    const totalEquipment = equipment.length;
    const available = equipment.filter(e => e.status === 'available').length;
    const inUse = equipment.filter(e => e.status === 'in_use').length;
    const maintenance = equipment.filter(e => e.status === 'maintenance').length;
    
    return { totalEquipment, available, inUse, maintenance };
  };

  const stats = getStatistics();

  return (
    <div>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Equipment"
              value={stats.totalEquipment}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Available"
              value={stats.available}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="In Use"
              value={stats.inUse}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Maintenance"
              value={stats.maintenance}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>Equipment Management</Title>
            <Badge count={equipment.length} showZero />
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder="Search equipment..."
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            
            <Select
              placeholder="Category"
              style={{ width: 150 }}
              value={selectedCategory}
              onChange={setSelectedCategory}
            >
              {categories.map(cat => (
                <Option key={cat.value} value={cat.value}>{cat.label}</Option>
              ))}
            </Select>

            <Select
              placeholder="Status"
              style={{ width: 150 }}
              value={selectedStatus}
              onChange={setSelectedStatus}
            >
              {statuses.map(status => (
                <Option key={status.value} value={status.value}>{status.label}</Option>
              ))}
            </Select>

            <Select
              placeholder="Block"
              style={{ width: 200 }}
              value={selectedBlock}
              onChange={setSelectedBlock}
            >
              <Option value="all">All Blocks</Option>
              {blocks.map(block => (
                <Option key={block.id} value={block.id}>
                  {block.block_code} - {block.block_name}
                </Option>
              ))}
            </Select>

            <Button
              icon={<ExportOutlined />}
              onClick={() => message.info('Export functionality coming soon')}
            >
              Export
            </Button>

            <Button
              icon={<ReloadOutlined />}
              onClick={fetchEquipment}
              loading={loading}
            >
              Refresh
            </Button>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/production/equipment/new')}
            >
              New Equipment
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredEquipment}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} equipment`
          }}
          scroll={{ x: 1300 }}
          rowClassName={(record) => {
            if (record.status === 'maintenance') return 'maintenance-row';
            if (record.status === 'out_of_service') return 'disabled-row';
            return '';
          }}
        />
      </Card>
    </div>
  );
};

export default EquipmentList;